import React from 'react'

import { ClickableLink } from '../../helpers/ClickableLink'
import useInstantOnboarding from '../../../hooks/useInstantOnboarding'
import useTranslationFunction from '../../../hooks/useTranslationFunction'
import {
  DEFAULT_CHATMAIL_HOSTNAME,
  DEFAULT_INSTANCE_PRIVACY_POLICY_URL,
  isQRWithDefaultInstance,
} from './chatmailInstances'
import { instanceDefaultChatmailHost } from '../../../utils/slothfulInstanceConfig'

import type { AccountQr, QrWithUrl } from '../../../backend/qr-types'

const URL_REGEX = /(https?:\/\/[^\s]+)/g

function extractLinkFromQrCode(qrWithUrl: QrWithUrl<AccountQr>) {
  if (qrWithUrl.qr.kind !== 'account') {
    throw new Error('QR needs to be of kind "account"')
  }

  const matches = qrWithUrl.url.match(URL_REGEX)
  if (matches && matches.length > 0) {
    const { origin } = new URL(matches[0])
    return origin
  }

  // webimap QR codes are `webimapaccount:host` (bare host, no scheme) — the
  // server is always reached over https, so make the link a real URL.
  if ((qrWithUrl.qr as any).webimap) {
    return `https://${qrWithUrl.qr.domain}`
  }

  return qrWithUrl.qr.domain
}

export default function UserAgreement() {
  const tx = useTranslationFunction()
  const { welcomeQr, selectedRelayHost } = useInstantOnboarding()
  const isDefaultInstance = isQRWithDefaultInstance(welcomeQr)

  // slothfulchat-web: the relay actually signed up on can differ from
  // upstream's default instance — because the operator configured their own
  // default relay (SLOTHFUL_DEFAULT_CHATMAIL), or because the user picked one
  // in the onboarding relay dropdown (RelayPicker). Both only apply when the
  // account is really created on that relay — i.e. the user didn't scan a
  // specific account/login QR (matching `configurationQR` in
  // useInstantOnboarding) — and then the consent link must name that relay
  // and point at ITS privacy policy. The dropdown choice wins over the
  // configured default, exactly like it does for account creation.
  const relayChoosable =
    welcomeQr?.qr.kind !== 'account' && welcomeQr?.qr.kind !== 'login'
  const pickedHost =
    isDefaultInstance && relayChoosable
      ? (selectedRelayHost ?? instanceDefaultChatmailHost())
      : undefined
  const configuredHost =
    pickedHost === DEFAULT_CHATMAIL_HOSTNAME ? undefined : pickedHost

  return (
    <>
      {isDefaultInstance && configuredHost && (
        <ClickableLink href={`https://${configuredHost}/privacy.html`}>
          {tx('instant_onboarding_agree_default2', configuredHost)}
        </ClickableLink>
      )}
      {isDefaultInstance && !configuredHost && (
        <ClickableLink href={DEFAULT_INSTANCE_PRIVACY_POLICY_URL}>
          {tx('instant_onboarding_agree_default2', DEFAULT_CHATMAIL_HOSTNAME)}
        </ClickableLink>
      )}
      {!isDefaultInstance && welcomeQr && welcomeQr.qr.kind === 'account' && (
        <ClickableLink
          href={extractLinkFromQrCode({ ...welcomeQr, qr: welcomeQr.qr })}
        >
          {tx('instant_onboarding_agree_instance', welcomeQr.qr.domain)}
        </ClickableLink>
      )}
    </>
  )
}
